import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { AdminTabs } from "../components/AdminTabs";
import { Button, Card, ErrorNote, InlineNote, Spinner } from "../components/primitives";
import { Markdown } from "../components/Markdown";
import { api } from "../lib/api";
import { date, money } from "../lib/format";
import type { AdminEvaluation } from "../lib/types";

/**
 * Evaluations — run a playbook against a document whose answers are already
 * verified, and record what you changed because of the result.
 *
 * The page has two jobs the API cannot do for it.
 *
 * The first is to make the *reference* choice obviously different from a data
 * room. Both are uploads, both are documents, and picking the wrong one is the
 * mistake that quietly invalidates the whole exercise — a run that can read
 * the answers scores perfectly. The API refuses it either way; this says why
 * before somebody tries.
 *
 * The second is the loop. A score on its own is a number. What makes it worth
 * running twice is the version beside it and the sentence saying what changed
 * between them, so every row carries its playbook fingerprint and every open
 * report carries the earlier evaluations of the same document above the box
 * you write the change into.
 */
export function Evaluations() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);

  const evaluations = useQuery({
    queryKey: ["admin-evaluations"],
    queryFn: api.adminEvaluations,
    // A run takes tens of minutes and the comparison lands after it, so the
    // page has to notice on its own rather than making somebody reload.
    refetchInterval: 15_000,
  });

  if (evaluations.isLoading) return <Spinner />;
  if (evaluations.error) return <ErrorNote error={evaluations.error} />;

  const rows = evaluations.data ?? [];

  return (
    <div className="max-w-read space-y-4">
      <AdminTabs />
      <div>
        <p className="text-sm text-ink-2">
          Run a playbook against a document whose findings are already verified
          — a VDD, or a CIM review somebody has checked — and read what the two
          disagree about. Market size, growth, the business plan, the
          competitive set and the conclusion; not wording. Then write down what
          you changed, so the next run is a comparison and not a fresh start.
        </p>
      </div>

      <Start
        // Which runs are already graded is knowledge this page has and the
        // form does not: offering one of them would put a 409 in front of
        // somebody for a choice the page could have not made available.
        evaluated={new Set(rows.map((row) => row.run_id))}
        onStarted={() =>
          queryClient.invalidateQueries({ queryKey: ["admin-evaluations"] })
        }
      />

      <Card title={`Evaluations (${rows.length})`}>
        {rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-ink-2">
            None yet. Upload a reference document to a{" "}
            <Link to="/data-rooms" className="underline">
              reference room
            </Link>{" "}
            first — a data room will not do, because its contents go into the
            knowledge graph and the run would be able to read the answers.
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {rows.map((row) => (
              <Row
                key={row.run_id}
                row={row}
                open={open === row.run_id}
                onToggle={() => setOpen(open === row.run_id ? null : row.run_id)}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function Row({
  row,
  open,
  onToggle,
}: {
  row: AdminEvaluation;
  open: boolean;
  onToggle: () => void;
}) {
  const report = useQuery({
    queryKey: ["admin-evaluation", row.run_id],
    queryFn: () => api.adminEvaluation(row.run_id),
    enabled: open,
  });

  const done = row.status === "completed" || row.status === "failed";

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">
            {row.target ?? row.run_id}
          </div>
          <div className="text-xs text-ink-2">
            {row.playbook}{" "}
            <Version version={row.playbook_version} existing={row.existing_run} />{" "}
            · against <span className="font-mono">{row.reference}</span>
            {row.existing_run && (
              <>
                {" · "}
                <span
                  className="text-muted"
                  title="This run already existed and was graded afterwards. It was not bought for the evaluation, and no fingerprint was taken when it started."
                >
                  graded after the fact
                </span>
              </>
            )}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-3">
          {/* The headline, or an honest account of why there isn't one yet.
              A blank cell reads as a failed evaluation. */}
          <span className="text-xs text-ink-2">
            {row.headline
              ? row.headline
              : done
                ? row.has_report
                  ? "report ready"
                  : "no comparison"
                : "running…"}
          </span>
          {row.cost_usd != null && (
            <span className="font-mono text-xs tabular-nums text-muted">
              {money(row.cost_usd)}
            </span>
          )}
          <Link
            to={`/engagements/${row.run_id}`}
            className="text-xs underline"
            title="The run itself"
          >
            run
          </Link>
          <Button size="sm" onClick={onToggle}>
            {open ? "Hide" : "Report"}
          </Button>
        </div>
      </div>

      {/* Shown on the closed row too: it is the reason the next evaluation
          exists, and burying it behind a click makes the list a list of
          scores again. */}
      {row.improvement && (
        <p className="mt-2 text-xs text-ink-2">
          <span className="text-muted">Changed after this: </span>
          {row.improvement}
        </p>
      )}

      {open && (
        <div className="mt-3 space-y-3 rounded-sm border border-line p-3">
          {report.isLoading && <Spinner />}
          {report.error && <ErrorNote error={report.error} />}
          {report.data && (
            <>
              {report.data.history.length > 0 && (
                <History rows={report.data.history} />
              )}
              {report.data.report ? (
                <Markdown content={report.data.report} />
              ) : (
                <p className="text-sm text-ink-2">
                  No comparison yet. It is written once the run finishes, so if
                  the run is still going this is the expected state.
                </p>
              )}
              <Improvement
                runId={row.run_id}
                current={report.data.improvement}
              />
            </>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * The playbook's fingerprint, or an honest blank.
 *
 * Never defaulted to the current version: an unknown shown as a question is
 * something somebody checks, and an unknown shown as an answer is wrong.
 */
function Version({
  version,
  existing,
}: {
  version: string | null;
  /** Graded after the fact, which is a *different* reason for a blank. */
  existing?: boolean;
}) {
  return (
    <span
      className="font-mono text-xs text-muted"
      title={
        version
          ? "The playbook's declaration and every prompt, hashed, as they were when this run started"
          : existing
            ? "This run was graded after it had finished, so no fingerprint was taken when it started — the prompts have moved since, and claiming today's would be a wrong answer where a blank is an honest one"
            : "This evaluation's row was not written, so which version produced it is not known"
      }
    >
      {version ? `@${version.slice(0, 7)}` : "@unknown"}
    </span>
  );
}

/** Earlier evaluations of this playbook against this same document. */
function History({ rows }: { rows: AdminEvaluation[] }) {
  return (
    <div className="rounded-sm border border-line bg-surface-2 p-3">
      <h3 className="label text-ink-2">
        Earlier runs against this document
      </h3>
      <ul className="mt-2 space-y-1">
        {rows.map((row) => (
          <li key={row.run_id} className="text-xs text-ink-2">
            <Version version={row.playbook_version} existing={row.existing_run} />{" "}
            <span className="text-ink">{row.headline ?? "no comparison"}</span>
            {row.improvement && <> — {row.improvement}</>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * What was changed in the playbook because of this evaluation.
 *
 * A textarea and nothing clever. The change is a sentence in a prompt and
 * there is no shorter true way to say which — a dropdown of phases would
 * record where and lose what.
 */
function Improvement({
  runId,
  current,
}: {
  runId: string;
  current: string | null;
}) {
  const queryClient = useQueryClient();
  const [text, setText] = useState(current ?? "");

  const save = useMutation({
    mutationFn: () => api.adminNoteImprovement(runId, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-evaluations"] });
      queryClient.invalidateQueries({ queryKey: ["admin-evaluation", runId] });
    },
  });

  return (
    <div>
      <label className="text-sm">
        <span className="text-ink-2">What did you change because of this?</span>
        <textarea
          className="mt-1 w-full rounded-sm border border-line bg-transparent px-2 py-1 text-sm"
          rows={2}
          placeholder="e.g. widened the TAM boundary in research.md to include liquid cooling"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      {save.error && <ErrorNote error={save.error} />}
      <div className="mt-2 flex items-center gap-3">
        <Button
          size="sm"
          disabled={save.isPending || text === (current ?? "")}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        <span className="text-xs text-ink-2">
          The next run picks up a new fingerprint on its own — this is the note
          that says why it differs.
        </span>
      </div>
    </div>
  );
}

/**
 * Two ways to start an evaluation, and the difference between them is what the
 * page has to make plain.
 *
 * **A new run** buys the engagement and grades it: the fingerprint is taken as
 * it starts, so the result is attributable to a version of the playbook. That
 * is the loop this feature is for.
 *
 * **A finished run** grades work that already exists. It costs a comparison
 * and nothing else, which is the whole point — the answer to "how did this
 * playbook do on that deal" was previously to buy the deal again. What it
 * cannot give back is the version: the run was made before anybody thought of
 * grading it, so the row says unknown and the list says why.
 */
type Mode = "new" | "existing";

function Start({
  onStarted,
  evaluated,
}: {
  onStarted: () => void;
  /** Runs that already carry an evaluation — offering one again is a 409. */
  evaluated: Set<string>;
}) {
  const [mode, setMode] = useState<Mode>("new");
  const references = useQuery({
    queryKey: ["admin-evaluation-references"],
    queryFn: api.adminEvaluationReferences,
  });
  const playbooks = useQuery({ queryKey: ["playbooks"], queryFn: api.playbooks });
  const runs = useQuery({
    queryKey: ["runs"],
    queryFn: api.runs,
    // Only asked for once the second mode is open: an admin's run list is
    // every run on the machine, and the common case is the first mode.
    enabled: mode === "existing",
  });

  const [roomId, setRoomId] = useState("");
  const [filename, setFilename] = useState("");
  const [target, setTarget] = useState("");
  const [playbook, setPlaybook] = useState("cdd");
  const [runId, setRunId] = useState("");

  const start = useMutation({
    mutationFn: () =>
      api.adminStartEvaluation({ room_id: roomId, filename, target, playbook }),
    onSuccess: () => {
      setTarget("");
      onStarted();
    },
  });

  const compare = useMutation({
    mutationFn: () =>
      api.adminEvaluateExisting({ run_id: runId, room_id: roomId, filename }),
    onSuccess: () => {
      setRunId("");
      onStarted();
    },
  });

  // Only reference rooms reach this list — the route serves no other kind. A
  // data room here would be an invitation to make the one mistake that
  // invalidates the result.
  const rooms = references.data ?? [];
  const chosen = rooms.find((r) => r.id === roomId);

  // A run that is still going has no deliverable to grade, and one that is
  // already an evaluation would be refused — so neither is offered. The
  // route refuses both regardless; this is about not putting the refusal in
  // front of somebody as a surprise.
  const gradable = (runs.data ?? []).filter(
    (run) => run.status === "completed" && !evaluated.has(run.run_id),
  );

  const pending = start.isPending || compare.isPending;

  return (
    <Card title="New evaluation">
      <div className="space-y-3 px-4 py-3">
        {references.isLoading ? (
          <Spinner />
        ) : (
          <>
            {/* **The empty state is a note, not a replacement.** It used to be
                returned *instead of* the form, so an admin with no reference
                room saw one paragraph: no mode switch, no run picker, and no
                way to tell that the runs were there and the document was the
                only thing missing. Reported as "I can choose neither of
                them" — which is what a form that is not on the page looks
                like. */}
            {rooms.length === 0 && (
              <p className="rounded-sm border border-line bg-surface-2 p-3 text-sm text-ink-2">
                <span className="font-medium text-ink">
                  No reference room yet.
                </span>{" "}
                Create one on the{" "}
                <Link to="/data-rooms" className="underline">
                  Documents
                </Link>{" "}
                page and upload the verified document to it. A data room will
                not do, and the reason is not a formality: its contents are
                ingested into the knowledge graph, so a run can retrieve them
                and agree with the answers perfectly.
              </p>
            )}

            {/* The choice is which run is graded, not which document — so it
                sits above the reference, where the reading order matches. */}
            <div className="flex gap-1 rounded-sm border border-line p-1 text-sm">
              {(
                [
                  ["new", "Run a playbook now"],
                  ["existing", "Grade a finished run"],
                ] as [Mode, string][]
              ).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setMode(key)}
                  aria-pressed={mode === key}
                  className={`flex-1 rounded-sm px-3 py-1 ${
                    mode === key
                      ? "bg-surface-2 font-medium text-ink"
                      : "text-ink-2 hover:bg-surface-2"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="text-ink-2">Reference</span>
                <select
                  className="mt-1 w-full rounded-sm border border-line bg-transparent px-2 py-1"
                  value={roomId}
                  disabled={rooms.length === 0}
                  onChange={(e) => {
                    setRoomId(e.target.value);
                    setFilename("");
                  }}
                >
                  <option value="">
                    {rooms.length === 0
                      ? "No reference room yet"
                      : "Choose a reference room…"}
                  </option>
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm">
                <span className="text-ink-2">Document</span>
                <select
                  className="mt-1 w-full rounded-sm border border-line bg-transparent px-2 py-1"
                  value={filename}
                  onChange={(e) => setFilename(e.target.value)}
                  disabled={!chosen}
                >
                  <option value="">
                    {chosen ? "Choose a document…" : "Pick a room first"}
                  </option>
                  {(chosen?.file_names ?? []).map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>

              {mode === "new" ? (
                <>
                  <label className="text-sm">
                    <span className="text-ink-2">Playbook</span>
                    <select
                      className="mt-1 w-full rounded-sm border border-line bg-transparent px-2 py-1"
                      value={playbook}
                      onChange={(e) => setPlaybook(e.target.value)}
                    >
                      {(playbooks.data ?? []).map((book) => (
                        <option key={book.key} value={book.key}>
                          {book.title}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="text-sm">
                    <span className="text-ink-2">Target</span>
                    <input
                      className="mt-1 w-full rounded-sm border border-line bg-transparent px-2 py-1"
                      placeholder="The company or market the document is about"
                      value={target}
                      onChange={(e) => setTarget(e.target.value)}
                    />
                  </label>
                </>
              ) : (
                /* No playbook and no target: the run has them already, and a
                   field that let somebody restate one would let them state it
                   wrongly. */
                <label className="text-sm sm:col-span-2">
                  <span className="text-ink-2">Finished run</span>
                  <select
                    className="mt-1 w-full rounded-sm border border-line bg-transparent px-2 py-1"
                    value={runId}
                    onChange={(e) => setRunId(e.target.value)}
                    disabled={runs.isLoading}
                  >
                    <option value="">
                      {runs.isLoading ? "Loading runs…" : "Choose a run…"}
                    </option>
                    {gradable.map((run) => (
                      <option key={run.run_id} value={run.run_id}>
                        {run.target ?? run.run_id} · {run.playbook}
                        {run.started_at
                          ? ` · ${date(run.started_at)}`
                          : ""}
                      </option>
                    ))}
                  </select>
                  {!runs.isLoading && gradable.length === 0 && (
                    <span className="mt-1 block text-xs text-ink-2">
                      No finished run is available to grade. A run that is still
                      going has no deliverable yet, and one that has already been
                      evaluated keeps the report it has.
                    </span>
                  )}
                </label>
              )}
            </div>

            {chosen && chosen.locked.includes(filename) && (
              <InlineNote tone="warning">
                {filename} is still password-protected, so there is nothing to
                compare against. Unlock it on the Documents page first.
              </InlineNote>
            )}

            {start.error && <ErrorNote error={start.error} />}
            {compare.error && <ErrorNote error={compare.error} />}

            <div className="flex items-center gap-3">
              {mode === "new" ? (
                <>
                  <Button
                    variant="primary"
                    disabled={!roomId || !filename || !target || pending}
                    onClick={() => start.mutate()}
                  >
                    {start.isPending ? "Starting…" : "Run and compare"}
                  </Button>
                  {/* Said before the button, not after it. This is a full
                      engagement charged to your own balance, and the comparison
                      on top of it. */}
                  <span className="text-xs text-ink-2">
                    A full engagement, charged to you, plus a few cents to
                    compare. The run does not see the reference.
                  </span>
                </>
              ) : (
                <>
                  <Button
                    variant="primary"
                    disabled={!roomId || !filename || !runId || pending}
                    onClick={() => compare.mutate()}
                  >
                    {compare.isPending ? "Comparing…" : "Compare"}
                  </Button>
                  <span className="text-xs text-ink-2">
                    The comparison only — a few cents. Nothing about the run
                    changes, and its playbook version is not recorded: it ran
                    before anybody thought of grading it.
                  </span>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
